import {useCallback, useEffect, useRef, useState} from "react";

interface PanelOptions {
  defaultWidth: number;
  min: number;
  max: number;
  /** Width of the rail when collapsed. */
  collapsedWidth: number;
  defaultCollapsed?: boolean;
  /** Which edge the drag handle sits on — decides the sign of the drag delta. */
  edge: "left" | "right";
}

const read = (key: string, fallback: any) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    // A private-mode or quota failure must not stop the chrome from rendering.
    return fallback;
  }
};

const write = (key: string, value: any) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Persisting a panel width is never worth throwing over. */
  }
};

/**
 * A side panel that remembers how wide the admin left it and whether it was collapsed.
 *
 * Dragging is wired to pointer events rather than mouse events so a trackpad, a pen and a
 * touch screen all behave the same, and the pointer is captured for the duration so the drag
 * survives the cursor leaving the 6px handle — which it will, constantly.
 */
export function usePanelSize(storageKey: string, options: PanelOptions) {
  const {defaultWidth, min, max, collapsedWidth, defaultCollapsed = false, edge} = options;

  const [width, setWidth] = useState<number>(() => read(`${storageKey}.width`, defaultWidth));
  const [collapsed, setCollapsed] = useState<boolean>(() => read(`${storageKey}.collapsed`, defaultCollapsed));
  const [dragging, setDragging] = useState(false);
  const origin = useRef<{x: number; width: number} | null>(null);

  useEffect(() => write(`${storageKey}.width`, width), [storageKey, width]);
  useEffect(() => write(`${storageKey}.collapsed`, collapsed), [storageKey, collapsed]);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    origin.current = {x: event.clientX, width: collapsed ? collapsedWidth : width};
    setDragging(true);
  }, [collapsed, collapsedWidth, width]);

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!origin.current) return;
    const delta = edge === "right"
      ? event.clientX - origin.current.x
      : origin.current.x - event.clientX;
    const next = Math.round(origin.current.width + delta);

    // Dragging past the lower bound is how you collapse it, and dragging back out is how you
    // restore it — so the handle does both jobs and the toggle button stays optional.
    if (next < min - 24) {
      setCollapsed(true);
      return;
    }

    setCollapsed(false);
    setWidth(Math.min(Math.max(next, min), max));
  }, [edge, max, min]);

  const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
    origin.current = null;
    setDragging(false);
  }, []);

  return {
    width: collapsed ? collapsedWidth : width,
    collapsed,
    dragging,
    toggle: () => setCollapsed((current) => !current),
    setCollapsed,
    /** Spread onto the drag handle element. */
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      role: "separator" as const,
      "aria-orientation": "vertical" as const,
    },
  };
}

export default usePanelSize;
